// Bordregels uit het prototype (proto-bord-logic.js, proto-common.js): wat op een kaart staat, welk label telt,
// kolommen per persoon en groepen per klant of bucket.
import { addDays, categoryOf, deadline, dow, meaningOf, priorityLabel, URGENCY, type Category, type Deadline } from "./planning";
import type { PlanningData, Task } from "./types";

export const LABEL_COLOR: Record<string, string> = {
  "on hold": "#db2777", waiting: "#dc2626", "to do": "#d97706", ongoing: "#16a34a", test: "#2563eb", "test validated": "#0d9488", prod: "#7c3aed",
};
export const labelColor = (n: string) => LABEL_COLOR[n.trim().toLowerCase()] || "#7a90a3";

export const catOf = (t: Task): Category => categoryOf(t.labels);

/**
 * Het label dat op de kaart staat: bij actief werk het eerste werklabel, anders het label dat de categorie geeft.
 * De betekenis van elk label komt uit Instellingen; een label om te negeren staat niet op de kaart.
 */
export function labelFor(t: Task): string | null {
  const cat = catOf(t);
  return t.labels.map((l) => l.name).find((n) => meaningOf(n) === cat) || null;
}

export function byUrgency(a: { t: Task; d: Deadline }, b: { t: Task; d: Deadline }) {
  return URGENCY[a.d.key] - URGENCY[b.d.key] || (a.d.key === "verlopen" ? a.t.end.localeCompare(b.t.end) : b.d.od - a.d.od) || Number(b.t.critical) - Number(a.t.critical);
}

/** Drie vrijdagen om uit te kiezen: de eerstvolgende, die erna, en vier weken na de eerste. */
export function fridays(today: string): string[] {
  let s = today;
  const out: string[] = [];
  while (out.length < 2) { s = addDays(s, 1); if (dow(s) === 4) out.push(s); }
  return [out[0], out[1], addDays(out[0], 28)];
}

export interface CardView {
  crit: boolean; prio: string | null; prioCls: string; dl: Deadline | null; dlText: string; dlCls: string;
  lbl: string | null; checklist: string | null; deps: number; atts: number; pending: boolean;
}

export function cardView(t: Task, data: PlanningData, inGroup: boolean, klantName: string): CardView {
  const cat = catOf(t), act = cat === "actief", d = deadline(t.end, data.today);
  const preds = data.deps.filter((x) => x.succ === t.id).length;
  const done = t.checklist.filter((c) => c.done).length;
  return {
    crit: t.critical && act,
    prio: act && t.priority <= 3 ? priorityLabel(t.priority) : null, prioCls: "p-prio p" + t.priority,
    dl: act || !inGroup ? d : null,
    dlText: act ? d.text : klantName,
    dlCls: act ? "p-dl" + (d.tone ? " t-" + d.tone : "") : "p-meta-i",
    lbl: labelFor(t),
    checklist: t.checklist.length ? done + "/" + t.checklist.length : null,
    deps: preds, atts: t.attachments.length, pending: !!t.pending,
  };
}

export type GroupBy = "klant" | "bucket";

export interface Column {
  key: string; personId: string | null; act: Task[]; parked: Task[]; closing: Task[];
  groups: { label: string; tasks: Task[] }[];
}

export function buildColumn(personId: string | null, tasks: Task[], data: PlanningData, groupBy: GroupBy, names: { klant: (id: string) => string; bucket: (id: string) => string }): Column {
  const mine = tasks.filter((t) => (personId ? t.owners.some((o) => o.personId === personId) : t.owners.length === 0));
  const withD = (t: Task) => ({ t, d: deadline(t.end, data.today) });
  const act = mine.filter((t) => catOf(t) === "actief").map(withD).sort(byUrgency).map((x) => x.t);
  const parked = mine.filter((t) => catOf(t) === "geparkeerd").map(withD).sort(byUrgency).map((x) => x.t);
  const closing = mine.filter((t) => catOf(t) === "afsluiten").sort((a, b) => names.klant(a.projectId).localeCompare(names.klant(b.projectId)));
  const groups: { label: string; tasks: Task[] }[] = [];
  for (const t of act) {
    const label = groupBy === "bucket" ? names.bucket(t.bucketId) : names.klant(t.projectId);
    let g = groups.find((x) => x.label === label);
    if (!g) { g = { label, tasks: [] }; groups.push(g); }
    g.tasks.push(t);
  }
  return { key: personId || "__none", personId, act, parked, closing, groups };
}
